/**
 * Phone numbers come back from the API as E.164 digits WITHOUT the leading
 * `+` — country code then national number, e.g. `919876543210`. That is the
 * right thing to store (see BReporter-Backend/src/utils/phone.ts for why the
 * country code must stay), but it is not something a human should have to read.
 *
 * These helpers are display-only. Never send a formatted string back to the
 * API — pass the raw value through.
 */

/** Digits only — tolerates `+`, spaces, dashes and brackets. */
export function phoneDigits(raw: string | number | null | undefined): string {
	return String(raw ?? '').replace(/\D/g, '');
}

/**
 * `919876543210` → `+91 98765 43210`.
 *
 * Indian numbers (91 + 10 digits, the overwhelming majority) get the familiar
 * 5-5 grouping. Anything else falls back to `+<digits>`: we deliberately do NOT
 * guess a country code length we can't verify, because splitting in the wrong
 * place is worse than not splitting at all. Empty input returns ''.
 */
export function formatPhone(raw: string | number | null | undefined): string {
	const d = phoneDigits(raw);
	if (!d) return '';
	if (d.length === 12 && d.startsWith('91')) {
		return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
	}
	if (d.length === 10) {
		// Stored without a country code (legacy rows) — group it, but don't
		// invent a "+91" that the data doesn't actually claim.
		return `${d.slice(0, 5)} ${d.slice(5)}`;
	}
	return `+${d}`;
}
